"use client";

import React, { useRef } from 'react';

// Компонент для отображения слайдера с числовым значением внутри
export const ThumbValueSlider = ({ 
  name, 
  value, 
  onChange, 
  icon,
  maxValue,
  min = 1, 
  max = 39, 
  step = 1,
  onMaxLimitReached
}: { 
  name: string; 
  value: number; 
  onChange: (value: number) => void; 
  icon: string;
  maxValue: number;
  min?: number; 
  max?: number; 
  step?: number;
  onMaxLimitReached?: () => void;
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  
  // Логируем при изменении значения для отладки
  const handleValueChange = (newValue: number) => {
    console.log(`ThumbValueSlider ${name}: меняем значение с ${value} на ${newValue}`);
    
    // Проверяем, изменилось ли значение, чтобы избежать лишних обновлений
    if (newValue !== value) {
      // Вызываем коллбэк для обновления значения
      onChange(newValue);
      
      // Вызываем onMaxLimitReached только при увеличении значения
      if (newValue > value) {
        console.log(`ThumbValueSlider ${name}: увеличение значения`);
        onMaxLimitReached?.();
      }
    }
  };
  
  // Обработчик клика по треку слайдера
  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!trackRef.current) return;
    
    const trackRect = trackRef.current.getBoundingClientRect();
    const clickPosition = e.clientX - trackRect.left;
    const trackWidth = trackRect.width;
    const percentage = Math.max(0, Math.min(1, clickPosition / trackWidth));
    const newValue = Math.round(min + percentage * (max - min));
    
    handleValueChange(newValue);
  };

  return (
    <div className="mb-6 flex items-center">
      <div className="w-10 h-10 mr-3 flex items-center justify-center">
        <img 
          src={`/icons/${icon}`} 
          alt={name} 
          className="w-full h-full object-contain"
        />
      </div>
      <div className="flex-1 pl-4 pr-4"> 
        <div 
          ref={trackRef}
          className="relative h-[16px] w-[90%] rounded-full bg-secondary cursor-pointer"
          onClick={handleTrackClick}
        >
          <div 
            className="absolute h-full bg-primary rounded-full"
            style={{ width: `${((value - min) / (max - min)) * 100}%` }}
          />
          <div 
            className="absolute top-1/2 transform -translate-y-1/2"
            style={{ 
              left: `calc(${((value - min) / (max - min)) * 100}% - 17px)`,
              touchAction: "none" 
            }}
          >
            <div 
              className="h-[37px] w-[37px] rounded-full border border-primary bg-card flex items-center justify-center"
              onMouseDown={(e) => {
                e.stopPropagation();
                
                const onMouseMove = (moveEvent: MouseEvent) => {
                  if (!trackRef.current) return;
                  
                  const trackRect = trackRef.current.getBoundingClientRect();
                  const position = moveEvent.clientX - trackRect.left;
                  const percentage = Math.max(0, Math.min(1, position / trackRect.width));
                  const newValue = Math.round(min + percentage * (max - min));
                  
                  handleValueChange(newValue);
                };
                
                const onMouseUp = () => {
                  document.removeEventListener("mousemove", onMouseMove);
                  document.removeEventListener("mouseup", onMouseUp);
                };
                
                document.addEventListener("mousemove", onMouseMove);
                document.addEventListener("mouseup", onMouseUp);
              }}
            >
              <span className="text-sm font-medium text-card-foreground">{value}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThumbValueSlider; 